import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Trash2, RefreshCw, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(value) {
  if (!value) return "\u2014";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function toolLabel(toolType) {
  if (toolType === "fact_finder") return "Fact Finder";
  if (toolType === "job_briefing") return "Job Briefing";
  if (toolType === "grievance_prep") return "Grievance Prep";
  return "Draft";
}

function ProgressBar({ value }) {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="dd-progress-wrap">
      <div className="dd-progress-top">
        <span>Progress</span>
        <span>{safeValue}%</span>
      </div>
      <div className="dd-progress-track">
        <div className="dd-progress-fill" style={{ width: `${safeValue}%` }} />
      </div>
    </div>
  );
}

export default function DraftsDashboard() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toolFilter, setToolFilter] = useState("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => { loadDrafts(); }, []);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/api/drafts/all`);
      setDrafts(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDrafts = useMemo(() => {
    return drafts.filter((draft) => {
      const matchesTool = toolFilter === "all" ? true : draft.tool_type === toolFilter;
      const haystack = [
        draft.title,
        draft.tool_type,
        draft.status,
        draft.meta?.location,
        draft.meta?.incident_date,
        draft.meta?.work_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = haystack.includes(search.toLowerCase());
      return matchesTool && matchesSearch;
    });
  }, [drafts, toolFilter, search]);

  const resumeDraft = (draft) => {
    if (draft.tool_type === "job_briefing" && draft._resume_data) {
      navigate("/job-briefing/new", { state: { resume: draft._resume_data } });
    } else if (draft.route) {
      navigate(draft.route);
    }
  };

  const exportPdf = (draftId) => {
    window.open(`${API}/api/fact-finder/drafts/${draftId}/pdf`, "_blank");
  };

  const exportGrievancePdf = (draftId) => {
    window.open(`${API}/api/grievance-builder/drafts/${draftId}/pdf`, "_blank");
  };

  const deleteDraft = async (draft) => {
    try {
      if (draft.tool_type === "fact_finder") {
        await axios.delete(`${API}/api/fact-finder/drafts/${draft.id}`);
      } else if (draft.tool_type === "job_briefing") {
        await axios.delete(`${API}/api/job-briefing/briefing/${draft.id}`);
      } else if (draft.tool_type === "grievance_prep") {
        await axios.delete(`${API}/api/grievance-builder/drafts/${draft.id}`);
      }
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
    } catch { /* silent */ }
  };

  return (
    <div className="dd-page" data-testid="drafts-dashboard">
      <style>{css}</style>

      <header className="dd-header">
        <Link to="/" className="dd-back" data-testid="back-home"><ArrowLeft size={16} /> Home</Link>
        <div className="dd-header-row">
          <div>
            <h1>Your Work</h1>
            <p>Resume saved Fact Finder cases and Job Briefings in one place.</p>
          </div>
          <button onClick={loadDrafts} className="dd-refresh" data-testid="refresh-btn">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="dd-toolbar" data-testid="toolbar">
        <select
          value={toolFilter}
          onChange={(e) => setToolFilter(e.target.value)}
          className="dd-select"
          data-testid="tool-filter"
        >
          <option value="all">All tools</option>
          <option value="fact_finder">Fact Finder</option>
          <option value="job_briefing">Job Briefing</option>
          <option value="grievance_prep">Grievance Prep</option>
        </select>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title, location, status, or type"
          className="dd-search"
          data-testid="search-input"
        />
        <span className="dd-count">{filteredDrafts.length} item{filteredDrafts.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="dd-empty" data-testid="loading">Loading drafts...</div>
      ) : filteredDrafts.length === 0 ? (
        <div className="dd-empty" data-testid="no-drafts">
          <h2>No saved drafts found</h2>
          <p>Start a Fact Finder or Job Briefing and it will show up here.</p>
        </div>
      ) : (
        <div className="dd-list" data-testid="drafts-list">
          {filteredDrafts.map((draft) => (
            <div key={`${draft.tool_type}-${draft.id}`} className="dd-card" data-testid={`draft-card-${draft.id}`}>
              <div className="dd-card-main">
                <div className="dd-card-info" onClick={() => resumeDraft(draft)}>
                  <div className="dd-badges">
                    <span className={`dd-tool-badge ${draft.tool_type}`}>{toolLabel(draft.tool_type)}</span>
                    <span className={`dd-status-badge ${draft.status || "draft"}`}>{draft.status || "draft"}</span>
                  </div>
                  <h3>{draft.title || "Untitled"}</h3>
                  <div className="dd-meta">
                    <span>Updated: {formatDate(draft.updated_at)}</span>
                    {draft.meta?.location && <span>Location: {draft.meta.location}</span>}
                    {draft.meta?.incident_date && <span>Incident: {draft.meta.incident_date}</span>}
                    {draft.meta?.work_type && <span>Work Type: {draft.meta.work_type}</span>}
                  </div>
                </div>

                <div className="dd-card-right">
                  <ProgressBar value={draft.progress} />
                  <div className="dd-card-actions">
                    <button className="dd-resume-btn" onClick={() => resumeDraft(draft)} data-testid={`resume-${draft.id}`}>
                      Resume
                    </button>
                    {draft.tool_type === "fact_finder" && (
                      <button className="dd-pdf-btn" onClick={() => exportPdf(draft.id)} data-testid={`pdf-${draft.id}`} title="Export PDF">
                        <Download size={14} />
                      </button>
                    )}
                    {draft.tool_type === "grievance_prep" && (
                      <button className="dd-pdf-btn" onClick={() => exportGrievancePdf(draft.id)} data-testid={`pdf-${draft.id}`} title="Export PDF">
                        <Download size={14} />
                      </button>
                    )}
                    <button className="dd-delete-btn" onClick={() => deleteDraft(draft)} data-testid={`delete-${draft.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const css = `
.dd-page { max-width: 960px; margin: 0 auto; padding: 20px 16px; min-height: 100vh; font-family: 'Inter', -apple-system, sans-serif; background: var(--surface-base); color: var(--text-primary); }
.dd-header { margin-bottom: 20px; }
.dd-back { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-secondary); text-decoration: none; border: 1px solid var(--border-default); padding: 6px 14px; border-radius: 8px; margin-bottom: 14px; transition: all 0.15s; }
.dd-back:hover { border-color: var(--border-hover); color: var(--text-primary); }
.dd-header-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.dd-header h1 { font-size: 28px; font-weight: 700; margin: 0 0 4px; }
.dd-header p { color: var(--text-secondary); font-size: 14px; margin: 0; }
.dd-refresh { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 10px; font-size: 13px; font-weight: 500; cursor: pointer; background: var(--surface-card); border: 1px solid var(--border-default); color: var(--text-secondary); transition: all 0.15s; }
.dd-refresh:hover { border-color: var(--border-hover); color: var(--text-primary); }

/* Toolbar */
.dd-toolbar { display: grid; grid-template-columns: 180px 1fr auto; gap: 10px; align-items: center; background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 14px; padding: 12px 14px; margin-bottom: 20px; }
.dd-select, .dd-search { background: var(--surface-base); border: 1px solid var(--border-default); color: var(--text-primary); border-radius: 10px; padding: 10px 14px; font-size: 14px; font-family: inherit; }
.dd-select:focus, .dd-search:focus { outline: none; border-color: var(--color-accent); }
.dd-select option { background: var(--surface-base); color: var(--text-primary); }
.dd-count { font-size: 12px; color: var(--text-muted); white-space: nowrap; }

/* Empty */
.dd-empty { background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 14px; padding: 40px 20px; text-align: center; color: var(--text-muted); }
.dd-empty h2 { font-size: 18px; font-weight: 600; color: var(--text-secondary); margin: 0 0 6px; }
.dd-empty p { font-size: 14px; margin: 0; }

/* Card list */
.dd-list { display: flex; flex-direction: column; gap: 10px; }
.dd-card { background: var(--surface-card); border: 1px solid var(--border-default); border-radius: 14px; padding: 18px; transition: all 0.15s; }
.dd-card:hover { border-color: var(--border-hover); }
.dd-card-main { display: flex; gap: 20px; flex-wrap: wrap; }
.dd-card-info { flex: 1; min-width: 0; cursor: pointer; }
.dd-badges { display: flex; gap: 6px; margin-bottom: 6px; flex-wrap: wrap; }
.dd-tool-badge { padding: 3px 10px; border-radius: 999px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.dd-tool-badge.fact_finder { background: rgba(245,158,11,0.1); color: #fbbf24; border: 1px solid rgba(245,158,11,0.25); }
.dd-tool-badge.job_briefing { background: rgba(34,211,238,0.1); color: #22d3ee; border: 1px solid rgba(34,211,238,0.25); }
.dd-tool-badge.grievance_prep { background: rgba(245,158,11,0.1); color: #fbbf24; border: 1px solid rgba(245,158,11,0.25); }
.dd-status-badge { padding: 3px 10px; border-radius: 999px; font-size: 10px; font-weight: 600; text-transform: uppercase; background: var(--surface-base); border: 1px solid var(--border-default); color: var(--text-muted); }
.dd-status-badge.completed, .dd-status-badge.complete { color: var(--color-success); border-color: rgba(34,197,94,0.25); background: rgba(34,197,94,0.08); }
.dd-status-badge.needs_review { color: #f59e0b; border-color: rgba(245,158,11,0.25); background: rgba(245,158,11,0.08); }
.dd-status-badge.ready_for_steward { color: #22c55e; border-color: rgba(34,197,94,0.25); background: rgba(34,197,94,0.08); }
.dd-card-info h3 { font-size: 17px; font-weight: 600; margin: 0 0 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dd-meta { display: flex; flex-wrap: wrap; gap: 6px 14px; font-size: 12px; color: var(--text-muted); }

/* Right side: progress + actions */
.dd-card-right { width: 260px; display: flex; flex-direction: column; gap: 10px; flex-shrink: 0; }
.dd-progress-wrap { width: 100%; }
.dd-progress-top { display: flex; justify-content: space-between; font-size: 11px; color: var(--text-muted); margin-bottom: 4px; }
.dd-progress-track { width: 100%; height: 6px; background: var(--surface-base); border-radius: 999px; overflow: hidden; }
.dd-progress-fill { height: 6px; background: var(--color-accent); border-radius: 999px; transition: width 0.3s; }
.dd-card-actions { display: flex; gap: 6px; }
.dd-resume-btn { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 4px; padding: 10px 16px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; background: var(--color-accent); border: none; color: var(--surface-base); transition: all 0.15s; }
.dd-resume-btn:hover { filter: brightness(1.1); }
.dd-delete-btn { background: none; border: 1px solid var(--border-default); color: var(--text-muted); padding: 10px; border-radius: 10px; cursor: pointer; transition: all 0.15s; display: flex; align-items: center; }
.dd-delete-btn:hover { border-color: var(--color-danger); color: var(--color-danger); }
.dd-pdf-btn { background: none; border: 1px solid rgba(34,197,94,0.25); color: #22c55e; padding: 10px; border-radius: 10px; cursor: pointer; transition: all 0.15s; display: flex; align-items: center; }
.dd-pdf-btn:hover { background: rgba(34,197,94,0.1); border-color: #22c55e; }

@media (max-width: 768px) {
  .dd-toolbar { grid-template-columns: 1fr; }
  .dd-card-main { flex-direction: column; }
  .dd-card-right { width: 100%; }
  .dd-card-actions { width: 100%; }
  .dd-resume-btn { flex: 1; }
}
`;
